import { zodResolver } from '@hookform/resolvers/zod';
import { mt, rd } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  REPORT_ENTITY_TYPES,
  type ReportEntityType,
  type ReportTemplate,
  type ReportTemplateDraft
} from 'v5/api/report-templates/reportTemplates.api';
import type { FieldNameTypeMapping } from 'v5/core/custom-fields/custom-fields-filters.http-converters';
import {
  buildReportFieldTree,
  listSelectableReportFields
} from 'v5/core/reporting/reportFieldTree';
import { Button } from 'v5/design-system/Button';
import { Form } from 'v5/design-system/Form';
import { Skeleton } from 'v5/design-system/Skeleton';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { FieldContextProvider } from 'v5/patterns/forms/schema-form/FieldContext';
import { SingleSelectField } from 'v5/patterns/forms/schema-form/fields/SingleSelectField';
import { TextField } from 'v5/patterns/forms/schema-form/fields/TextField';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { useMemoCompare } from 'v5/platform/lang/useMemoCompare';
import { WithServices } from 'v5/platform/ts/services';
import { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithBookingTypeService } from 'v5/services/io/BookingTypeService/BookingTypeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { ReportColumnsField } from './ReportColumnsField';
import { ReportFiltersField } from './ReportFiltersField';
import {
  newReportTemplateFormValues,
  reportTemplateDraftOf,
  reportTemplateFormValuesOf,
  type ReportTemplateFormValues,
  withReportEntityType
} from './reportTemplateEditorValues';
import {
  reportTypedEntityOf,
  useReportTemplateCatalog
} from './useReportTemplateCatalog';

export type ReportTemplateEditorMode =
  | { type: 'create' }
  | { type: 'edit'; template: ReportTemplate }
  | { type: 'view'; template: ReportTemplate };

export type ReportTemplateEditorDialogContentProps = WithServices<
  [
    WithAppScopeService,
    WithAccessService,
    WithResourceTypeService,
    WithProductionTypeService,
    WithBookingTypeService,
    WithMetastructureMappingService,
    WithResourceService,
    WithTagService,
    WithGroupsService,
    WithEnterpriseService
  ]
> & {
  open: boolean;
  /** Called once the template is saved (its form reset), and by "Close" in view mode. */
  close: () => void;
  mode: ReportTemplateEditorMode;
  /** Saves the template; the dialog closes when it resolves and shows the error when it throws. */
  onSubmit: (draft: ReportTemplateDraft) => Promise<void>;
};

const templateOf = (mode: ReportTemplateEditorMode) =>
  mode.type === 'create' ? null : mode.template;

/** Creates, edits or shows (read-only) a report template: what it reports on, its filters and columns. */
export function ReportTemplateEditorDialogContent({
  open,
  close,
  mode,
  onSubmit,
  services
}: ReportTemplateEditorDialogContentProps) {
  const { t } = useTranslation('reporting', {
    keyPrefix: 'ReportTemplateEditor'
  });
  const { t: tReporting } = useTranslation('reporting');
  const template = templateOf(mode);
  const initialValues = useMemoCompare(
    template
      ? reportTemplateFormValuesOf(template)
      : newReportTemplateFormValues()
  );

  const form = useForm<ReportTemplateFormValues>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(
      z.object({
        name: z.string().trim().min(1, t('nameRequired')),
        description: z.string(),
        entityType: z.enum(REPORT_ENTITY_TYPES),
        typeId: z.string().nullable(),
        query: z.custom<ReportTemplateFormValues['query']>(),
        fields: z
          .array(z.object({ key: z.string(), name: z.string().nullable() }))
          .min(1, t('columnsRequired'))
      })
    )
  });
  useEffect(() => {
    form.reset(initialValues);
  }, [form, initialValues]);
  useRegisterDirtyState(form.formState.isDirty);

  useEffect(() => {
    // only the user's pick resets the rest: a reset or a seed is no change of mind
    const subscription = form.watch((values, { name, type }) => {
      if (name !== 'entityType' || type !== 'change' || !values.entityType) {
        return;
      }
      const next = withReportEntityType(form.getValues(), values.entityType);
      form.setValue('typeId', next.typeId, { shouldDirty: true });
      form.setValue('query', next.query, { shouldDirty: true });
      form.setValue('fields', next.fields, { shouldDirty: true });
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const entityType = useWatch({ control: form.control, name: 'entityType' });
  const typeId = useWatch({ control: form.control, name: 'typeId' });
  const typedEntity = reportTypedEntityOf(entityType);
  const catalog = useReportTemplateCatalog(services);
  const columnOptions = rd.useMemoMap(
    catalog,
    (catalog, entityType: ReportEntityType, typeId: string | null) =>
      listSelectableReportFields(
        buildReportFieldTree({
          entityType,
          selectedTypeId: typeId === null ? null : Number(typeId),
          types: catalog.types,
          customFieldDefinitions: catalog.customFieldDefinitions,
          customFieldGroupDefinitions: catalog.customFieldGroupDefinitions,
          allowCheckins: catalog.allowCheckins
        })
      ),
    entityType,
    typeId
  );
  const typeOptions = useMemo(
    () =>
      typedEntity
        ? (rd.tryGet(catalog)?.types[typedEntity] ?? [])
            .filter(type => !type.archived || String(type.id) === typeId)
            .map(type => ({
              value: String(type.id),
              label: type.archived
                ? t('archivedType', { name: type.name })
                : type.name
            }))
        : [],
    [catalog, typedEntity, typeId, t]
  );

  const mutation = promiseState.useMutation(
    async ({
      values,
      fieldTypes
    }: {
      values: ReportTemplateFormValues;
      fieldTypes: FieldNameTypeMapping;
    }) => {
      await onSubmit(reportTemplateDraftOf(values, template, fieldTypes));
      // Saved: closing must not ask to discard it.
      form.reset(values);
      close();
    }
  );

  const readOnly = mode.type === 'view';
  const title = {
    create: t('titleCreate'),
    edit: t('titleEdit'),
    view: t('titleView')
  }[mode.type];

  return (
    <Form {...form}>
      <FormDialogContent
        open={open}
        title={title}
        onSubmit={form.handleSubmit(values => {
          // The button only looks disabled: Enter and a second click land here.
          if (readOnly || mt.isInProgress(mutation.state)) return;
          // Saving needs each custom field's type; until the catalog loads there is nothing to save with.
          const fieldTypes = rd.tryGet(catalog)?.fieldTypes;
          if (fieldTypes === undefined) return;
          // A failure is shown above the button from the mutation's state.
          mutation.track({ values, fieldTypes }).catch(() => {});
        })}
        mutation={mutation.state}
        saveText={mode.type === 'create' ? t('create') : t('save')}
        saveButtonSlot={
          readOnly ? (
            <Button type="button" variant="secondary" onClick={close}>
              {t('close')}
            </Button>
          ) : undefined
        }
      >
        <FieldContextProvider
          value={{
            mode: readOnly ? 'display' : 'edit',
            variant: 'regular',
            size: 'md',
            basePath: '',
            ghost: false
          }}
        >
          <TextField
            spec={{
              key: 'name',
              label: t('name'),
              description: null,
              required: true,
              multiline: false,
              placeholder: t('namePlaceholder')
            }}
            path="name"
          />
          <TextField
            spec={{
              key: 'description',
              label: t('description'),
              description: null,
              required: false,
              multiline: true,
              placeholder: t('descriptionPlaceholder')
            }}
            path="description"
          />
          <div className="grid grid-cols-2 gap-4">
            <SingleSelectField
              spec={{
                key: 'entityType',
                label: t('reportOn'),
                description: null,
                required: true,
                clearable: false,
                options: REPORT_ENTITY_TYPES.map(value => ({
                  value,
                  label: tReporting(`entityTypes.${value}`)
                }))
              }}
              path="entityType"
            />
            {typedEntity && (
              <SingleSelectField
                spec={{
                  key: 'typeId',
                  label: t(`typeLabel.${typedEntity}`),
                  description: null,
                  required: false,
                  clearable: true,
                  placeholder: t(`typeAll.${typedEntity}`),
                  options: typeOptions
                }}
                path="typeId"
              />
            )}
          </div>
          {rd
            .journey(catalog)
            .wait(<Skeleton className="h-8 w-64" />)
            .catch(error => (
              <p className="text-sm text-fg-danger">{error.message}</p>
            ))
            .map(catalog => (
              <ReportFiltersField
                spec={{
                  key: 'query',
                  label: t('filters'),
                  description: t('filtersHint'),
                  required: false
                }}
                path="query"
                entityType={entityType}
                fieldTypes={catalog.fieldTypes}
                services={services}
              />
            ))}
          {rd
            .journey(columnOptions)
            .wait(<Skeleton className="h-48 w-full" />)
            .catch(() => null)
            .map(options => (
              <ReportColumnsField
                spec={{
                  key: 'fields',
                  label: t('columns'),
                  description: t('columnsHint'),
                  required: true
                }}
                path="fields"
                options={options}
              />
            ))}
        </FieldContextProvider>
      </FormDialogContent>
    </Form>
  );
}
